import { FunctionComponent, forwardRef, useEffect, useRef } from 'react';
import type { JSX, ReactNode, SVGProps } from 'react';

import classNames from 'classnames';

import CloseIcon from '@admin-platform/ui/icons/outline/x-mark.icon.svg?react';
// TODO: Add info and warning icons to libs/ui/icons/src/solid/
// import InfoIcon from '@admin-platform/ui/icons/solid/info.icon.svg?react';
// import WarningIcon from '@admin-platform/ui/icons/solid/warning.icon.svg?react';

import styles from './Alert.module.css';

export const AlertTypes = [
  'error',
  'information',
  'success',
  'warning',
] as const;
export type AlertType = (typeof AlertTypes)[number];

interface AlertProps {
  type: AlertType;
  className?: string;
  onDismiss?(): void;
  dismissAfter?: number | null;
  children?: ReactNode;
  scrollIntoView?: boolean;
  icon?: FunctionComponent<SVGProps<SVGSVGElement>>;
  showIcon?: boolean;
}

interface AlertContextProps {
  type: AlertType;
  className?: string;
  onDismiss?(): void;
  children?: ReactNode;
  icon?: FunctionComponent<SVGProps<SVGSVGElement>>;
  showIcon?: boolean;
}

// TODO: Re-enable once info and warning icons are added
// const MAPPER_ICONS: Record<
//   AlertType,
//   FunctionComponent<SVGProps<SVGSVGElement>>
// > = {
//   error: WarningIcon,
//   information: InfoIcon,
//   success: InfoIcon,
//   warning: InfoIcon,
// };

const AlertContent = forwardRef<HTMLDivElement, AlertContextProps>(
  function AlertContentInner(
    { children, className, type, onDismiss, icon, showIcon },
    ref,
  ) {
    // TODO: Re-enable MAPPER_ICONS once info and warning icons are added
    const Icon = icon; // ?? MAPPER_ICONS[type];

    return (
      <div
        ref={ref}
        className={classNames(styles.Alert, className, {
          [styles[type]]: type,
        })}
        role="alert"
      >
        <div className={styles.AlertContent}>
          {showIcon && Icon && <Icon className={styles.Icon} />}
          {children}
        </div>

        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className={styles.CloseButton}
          >
            <CloseIcon />
          </button>
        )}
      </div>
    );
  },
);

export default function Alert({
  onDismiss,
  dismissAfter = 7000,
  scrollIntoView = true,
  ...props
}: AlertProps): JSX.Element {
  const alertRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let dismissTimerId: ReturnType<typeof setTimeout>;

    if (onDismiss && dismissAfter) {
      dismissTimerId = setTimeout(onDismiss, dismissAfter);
    }

    if (scrollIntoView && alertRef.current) {
      alertRef.current.scrollIntoView({
        behavior: 'smooth',
      });
    }

    return () => clearTimeout(dismissTimerId);
  }, [dismissAfter, onDismiss, scrollIntoView]);

  return <AlertContent ref={alertRef} onDismiss={onDismiss} {...props} />;
}
